import type { Marker, MarkerMeasurement } from '@/lib/measurementTypes';

// Sample data for the landing page. It feeds the app's own chart components, so the pictures
// show exactly what the app draws, and it is what the app would store for such reports: each
// marker under its reference name and category (api/reference/reference_markers.json), its
// values converted to that reference's units, spelled as pint writes them ("mg / dl"). The
// values came from running the backend's Measurement model on lab report input: 14.8 g/dL is
// stored as 148 g / l, and 7.2 10^3/µL as 7200 1 / µl. Relative value and out of range are
// worked out the way the backend's set_relative_value and set_out_of_range do. The app lists
// a report's markers by name, so each list below is in that order.

type Range = [min: number, max: number];

const ENDOCRINOLOGY = 'Endocrinology';
const HEMATOLOGY = 'Hematology';
const IMMUNOLOGY = 'Immunology';
const IONS = 'Ions and trace metals';
const LIPIDS = 'Lipids';
const LIVER = 'Liver function';
const METABOLITES = 'Other electrolytes and metabolites';
const VITAMINS = 'Vitamins';

function measurement(
  name: string,
  category: string,
  units: string,
  [min, max]: Range,
  datetime: string,
  value: number
): MarkerMeasurement {
  return {
    id: `${name} ${datetime}`,
    name,
    category,
    datetime,
    original_text: '',
    value,
    units,
    base_units: units,
    range_min: min,
    range_max: max,
    out_of_range: String(value < min || value > max),
    relative_value: (value - min) / (max - min),
    errors: null,
    is_encrypted: false,
  };
}

function marker(
  name: string,
  category: string,
  units: string,
  range: Range,
  dates: string[],
  values: number[]
): Marker {
  return {
    name,
    category,
    units,
    reference_id: name,
    measurements: values.map((value, index) =>
      measurement(name, category, units, range, dates[index], value)
    ),
  };
}

export const SAMPLE_DATES = ['2024-06-15', '2024-12-01', '2025-03-10'];

// The report table: six markers across three reports.
export const SAMPLE_REPORT: Marker[] = [
  marker(
    'Glucose',
    METABOLITES,
    'mg / dl',
    [70, 99],
    SAMPLE_DATES,
    [105, 98, 112]
  ),
  marker(
    'Hemoglobin (Hb)',
    HEMATOLOGY,
    'g / l',
    [120, 175],
    SAMPLE_DATES,
    [142, 138, 151]
  ),
  marker(
    'LDL cholesterol',
    LIPIDS,
    'mg / dl',
    [0, 130],
    SAMPLE_DATES,
    [135, 120, 110]
  ),
  marker(
    'Platelet count (Plt)',
    HEMATOLOGY,
    '1 / nl',
    [150, 400],
    SAMPLE_DATES,
    [245, 220, 260]
  ),
  marker(
    'Thyroid stimulating hormone (TSH / Thyrotropin)',
    ENDOCRINOLOGY,
    'mIU / l',
    [0.4, 4],
    SAMPLE_DATES,
    [2.1, 2.8, 2.5]
  ),
  marker(
    'White blood cells (WBC)',
    HEMATOLOGY,
    '1 / µl',
    [4500, 11000],
    SAMPLE_DATES,
    [11200, 7500, 6800]
  ),
];

// The category list beside the table, as the app builds it: each of the table's categories
// once, by name.
export const SAMPLE_CATEGORIES = Array.from(
  new Set(SAMPLE_REPORT.map(marker => marker.category))
).sort((a, b) => a.localeCompare(b, 'en'));

// One report for the record chart. The landing page shows as many of its markers as fit the
// chart's width, from the first, so which markers make up the report matters: in the app's
// order, the first five, all a 320 px phone has room for, include a high and a low. Thirty
// cover the widest the chart gets at the default font size, 29 bars when its row stacks just
// below lg. Five are out of range, three high and two low.
const RECORD_DATE = ['2025-03-10'];

export const SAMPLE_RECORD: Marker[] = [
  marker(
    'Alanine transaminase (ALT / ALAT)',
    LIVER,
    'U / l',
    [7, 56],
    RECORD_DATE,
    [62]
  ),
  marker(
    'Aspartate transaminase (AST / ASAT)',
    LIVER,
    'IU / l',
    [10, 40],
    RECORD_DATE,
    [24]
  ),
  marker(
    'C-reactive protein (CRP)',
    IMMUNOLOGY,
    'mg / l',
    [0, 5],
    RECORD_DATE,
    [1.2]
  ),
  marker(
    'Calcium (Ca) - total',
    IONS,
    'mg / dl',
    [8.6, 10.3],
    RECORD_DATE,
    [8.4]
  ),
  marker('Cholesterol', LIPIDS, 'mg / dl', [0, 200], RECORD_DATE, [185]),
  marker('Creatinine', METABOLITES, 'mg / dl', [0.6, 1.2], RECORD_DATE, [0.9]),
  marker('Ferritin', IONS, 'ng / ml', [24, 336], RECORD_DATE, [45]),
  marker(
    'Free thyroxine (Free T4)',
    ENDOCRINOLOGY,
    'ng / dl',
    [0.8, 1.8],
    RECORD_DATE,
    [1.2]
  ),
  marker(
    'Gamma glutamyl transferase (GGT)',
    LIVER,
    'U / l',
    [9, 48],
    RECORD_DATE,
    [20]
  ),
  marker('Glucose', METABOLITES, 'mg / dl', [70, 99], RECORD_DATE, [108]),
  marker(
    'Glycated hemoglobin (HbA1c) - DCCT',
    HEMATOLOGY,
    '%',
    [4, 5.6],
    RECORD_DATE,
    [5.4]
  ),
  marker('HDL cholesterol', LIPIDS, 'mg / dl', [40, 100], RECORD_DATE, [55]),
  marker('Hematocrit (Hct)', HEMATOLOGY, '', [0.36, 0.5], RECORD_DATE, [0.44]),
  marker(
    'Hemoglobin (Hb)',
    HEMATOLOGY,
    'g / l',
    [120, 175],
    RECORD_DATE,
    [148]
  ),
  marker('LDL cholesterol', LIPIDS, 'mg / dl', [0, 130], RECORD_DATE, [136]),
  marker('Magnesium (Mg)', IONS, 'mg / dl', [1.7, 2.2], RECORD_DATE, [2]),
  marker(
    'Mean corpuscular volume (MCV)',
    HEMATOLOGY,
    'fl',
    [80, 100],
    RECORD_DATE,
    [88]
  ),
  marker(
    'Platelet count (Plt)',
    HEMATOLOGY,
    '1 / nl',
    [150, 400],
    RECORD_DATE,
    [240]
  ),
  marker('Potassium (K)', IONS, 'mmol / l', [3.5, 5.1], RECORD_DATE, [4.2]),
  marker(
    'Red blood cells (RBCs / Erythrocytes)',
    HEMATOLOGY,
    '1 / nl',
    [4200, 5800],
    RECORD_DATE,
    [4900]
  ),
  marker('Sodium (Na)', IONS, 'mmol / l', [135, 145], RECORD_DATE, [140]),
  marker(
    'Thyroid stimulating hormone (TSH / Thyrotropin)',
    ENDOCRINOLOGY,
    'mIU / l',
    [0.4, 4],
    RECORD_DATE,
    [2.2]
  ),
  marker('Total bilirubin', LIVER, 'mg / dl', [0.1, 1.2], RECORD_DATE, [0.7]),
  marker('Triglycerides', LIPIDS, 'mg / dl', [0, 150], RECORD_DATE, [110]),
  marker('Urea', METABOLITES, 'mg / dl', [17, 43], RECORD_DATE, [30]),
  marker('Uric acid', METABOLITES, 'mg / dl', [3.5, 7.2], RECORD_DATE, [5.1]),
  marker(
    'Vitamin B12 (Cobalamin)',
    VITAMINS,
    'ng / l',
    [200, 900],
    RECORD_DATE,
    [420]
  ),
  marker(
    'Vitamin B9 (Folate) – Serum',
    VITAMINS,
    'ng / ml',
    [3, 17],
    RECORD_DATE,
    [9]
  ),
  marker(
    'Vitamin D (25-hydroxycholecalciferol)',
    VITAMINS,
    'ng / ml',
    [30, 100],
    RECORD_DATE,
    [22]
  ),
  marker(
    'White blood cells (WBC)',
    HEMATOLOGY,
    '1 / µl',
    [4500, 11000],
    RECORD_DATE,
    [7200]
  ),
];
